import gql from 'graphql-tag';

import {apolloClient} from '../apolloClient';
import {PreviewableLinkResourceType} from '../generated/graphql';
import {propagateErrorsContext} from './errors';

type CreatePreviewableLinkInput = {
  previewImage?: Blob;
  entityName: string;
  title: string;
  description: string;
  resourceID: string;
  redirectURL: string;
  author: string;
  resourceType: PreviewableLinkResourceType;
  useDefaultImage?: boolean;
};

export const createPreviewableLink = async ({
  previewImage,
  entityName,
  title,
  description,
  resourceID,
  resourceType,
  redirectURL,
  author,
  useDefaultImage = false,
}: CreatePreviewableLinkInput) => {
  if (useDefaultImage && previewImage != null) {
    throw new Error('Cannot use default image and provide custom image');
  }
  if (!useDefaultImage && previewImage == null) {
    throw new Error('Must provide custom image if not using default image');
  }
  const {link, previewImageUploadURL} =
    (
      await apolloClient.mutate({
        context: propagateErrorsContext(),
        mutation: gql`
          mutation CreatePreviewableLink(
            $entityName: String!
            $title: String!
            $description: String!
            $resourceID: ID!
            $resourceType: PreviewableLinkResourceType!
            $redirectURL: String!
            $useDefaultImage: Boolean!
            $author: String!
          ) {
            createPreviewableLink(
              input: {
                entityName: $entityName
                title: $title
                description: $description
                resourceID: $resourceID
                resourceType: $resourceType
                useDefaultImage: $useDefaultImage
                redirectURL: $redirectURL
                author: $author
              }
            ) {
              link
              previewImageUploadURL
            }
          }
        `,
        variables: {
          entityName,
          title,
          description,
          resourceID,
          resourceType,
          useDefaultImage,
          redirectURL,
          author,
        },
      })
    )?.data?.createPreviewableLink ?? {};

  if (link == null || (!useDefaultImage && previewImageUploadURL == null)) {
    throw new Error('Unable to upload panel');
  }

  if (useDefaultImage) {
    return link as string;
  }

  const headers: Record<string, string> = {
    'Content-Type': 'image/png',
    'x-goog-acl': 'public-read',
  };
  // eslint-disable-next-line wandb/no-unprefixed-urls
  await fetch(previewImageUploadURL, {
    method: 'PUT',
    headers,
    body: previewImage,
  });

  return link as string;
};
